import { Dispatch } from 'redux';
import { useDispatch } from 'react-redux';
import { useMemo } from 'react';
import CrudActionContainer from './CrudActionContainer';
import commonHandler, {
  permissionsHandler,
} from '../errorHandlers/common-handler';
import { adConfigsHandler } from '../errorHandlers/ad-configs-handler';
import AdConfigController, {
  SearchEntityType,
} from '../controllers/AdConfigController';
import { StoreState } from '../store-interfaces';
import AdConfigModel, { ExtendedAdConfigModel } from '../model/AdConfigModel';
import MessengerAdTemplateModel from '../model/MessengerAdTemplateModel';
import { selectAdConfig, unselectAdConfig } from './viewActions';

type AdConfigsResponse = { adConfigs: AdConfigModel[] };
type MessengerAdTemplatesResponse = { adTemplates: MessengerAdTemplateModel[] };

class AdConfigCRUDActions extends CrudActionContainer {
  resetAction: string;
  retrieveTemplatesAction: string;
  retrievedTemplatesAction: string;

  constructor() {
    super('AD_CONFIGS');
    this.resetAction = super.makeActionKey('RESET');
    this.retrieveTemplatesAction = super.makeActionKey('RETRIEVE_TEMPLATES');
    this.retrievedTemplatesAction = super.makeActionKey('RETRIEVED_TEMPLATES');
  }

  search(
    query: string,
    specialCategory: boolean = false,
    entity?: SearchEntityType,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await AdConfigController.search(
          query,
          specialCategory,
          entity,
        );
        return response.locations;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  create(botId: number, entity: Partial<ExtendedAdConfigModel>) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));
        const response = await AdConfigController.postAdConfig(botId, entity);
        const { adConfig } = response;
        dispatch(this.retrieved(adConfig));
        dispatch(selectAdConfig(adConfig));
        return adConfig;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  prepareReplacementPage(botId: number, entityId: number, pageId?: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.updateAction));
        const response = await AdConfigController.prepareReplacementPage(
          botId,
          entityId,
          pageId,
        );
        const { adConfig } = response;
        dispatch(this.retrieved(adConfig));
        dispatch(selectAdConfig(adConfig));
        return adConfig;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  retrieveAll(botId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));
      try {
        const response: AdConfigsResponse = await AdConfigController.getAll(
          botId,
        );
        const { adConfigs } = response;
        dispatch(this.retrievedAll(adConfigs));
        return adConfigs;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  retrieveById(botId: number, adConfigId: number, unbusy: boolean = true) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAction));
      try {
        const response = await AdConfigController.getById(botId, adConfigId);
        const { adConfig } = response;
        if (unbusy) {
          dispatch(this.updateLocal(adConfig));
          dispatch(selectAdConfig(adConfig));
        }
        return adConfig;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  update(
    botId: number,
    entity: Partial<ExtendedAdConfigModel>,
    draft: boolean = true,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));
      try {
        const response = await AdConfigController.putAdConfig(botId, entity);
        if (draft) {
          const { adConfig } = response;
          dispatch(this.updateLocal(adConfig));
          dispatch(selectAdConfig(adConfig));
          return adConfig;
        }
      } catch (e) {
        return adConfigsHandler(dispatch, this)(e);
      }
    };
  }

  publish(botId: number, adConfigId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));
      try {
        await AdConfigController.publish(botId, adConfigId);
      } catch (e) {
        return permissionsHandler(dispatch, this)(e);
      }
    };
  }

  delete(botId: number, adConfig: Partial<AdConfigModel>) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.deleteLocalAction));
      try {
        const response = await AdConfigController.deleteAdConfig(
          botId,
          adConfig.id as number,
        );
        dispatch(this.deleteLocal(adConfig));
        dispatch(unselectAdConfig());
        return response;
      } catch (e) {
        permissionsHandler(dispatch, this)(e);
      }
    };
  }

  retrieveTemplates(botId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveTemplatesAction));
      try {
        const response: MessengerAdTemplatesResponse =
          await AdConfigController.getTemplates(botId);
        dispatch(this.retrievedTemplatesActionCreator(response));
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  retrievedTemplatesActionCreator({
    adTemplates,
  }: MessengerAdTemplatesResponse) {
    return {
      type: this.retrievedTemplatesAction,
      templates: adTemplates,
    };
  }

  reset() {
    return {
      type: this.resetAction,
    };
  }

  getVacantAdsCoupons(facebookPageId?: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        return await AdConfigController.getVacantAdsCoupons(facebookPageId);
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  attachAdCoupon(governmental: boolean, facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        await AdConfigController.attachAdCoupon(governmental, facebookPageId);
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }
}

const adConfigCRUDActions = new AdConfigCRUDActions();

export const useAdConfigCRUDActions = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  return useMemo(
    () => ({
      retrieveAll: (chatbotId: number) =>
        dispatch(adConfigCRUDActions.retrieveAll(chatbotId)),
      getVacantAdsCoupons: (facebookPageId?: number) =>
        dispatch(adConfigCRUDActions.getVacantAdsCoupons(facebookPageId)),
      attachAdCoupon: (governmental: boolean, facebookPageId: number) =>
        dispatch(
          adConfigCRUDActions.attachAdCoupon(governmental, facebookPageId),
        ),
      create: (...params: Parameters<typeof adConfigCRUDActions.create>) =>
        dispatch(adConfigCRUDActions.create(...params)),
      delete: (...params: Parameters<typeof adConfigCRUDActions.delete>) =>
        dispatch(adConfigCRUDActions.delete(...params)),
    }),
    [dispatch],
  );
};

export default adConfigCRUDActions;
